import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import context from "../../store/context";

// layout
import AuthenticationLayout from "./AuthenticationLayout";

import AuthenticationButton from "../../components/Buttons/AuthenticationButton";
import AlertModal from "../../components/UI/AlertModal";

//icons
import ArrowIcon from "../../components/icons/Arrow";

//assets
import CBELogo from "../../assets/CBE.png";
import LoginPattern from "../../assets/login-pattern.svg";

// shared with the merchant on account creation
const MINIAPP_CODE = process.env.REACT_APP_MINIAPP_CODE;
const MINIAPP_ENDPOINT = process.env.REACT_APP_MINIAPP_ENDPOINT;

// give up if the SuperApp never calls our callback back
const LOGIN_TIMEOUT_MS = 20000;

const LoginPage = () => {
  const [isSending, setIsSending] = useState(false);
  const [alert, setAlert] = useState(null);

  const timeoutRef = useRef(null);

  const { setCbePhone, setToken } = useContext(context);

  const navigate = useNavigate();

  // stop the spinner + clear any pending timeout, then surface the message
  const showError = (title, message) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSending(false);
    setAlert({ title, message });
  };

  // log the customer in using the identifier provided by the SuperApp
  const cbeSuperAppLogin = async (customerIdentifier) => {
    sessionStorage.setItem("cbeToken", customerIdentifier);

    try {
      const res = await fetch(
        `${MINIAPP_ENDPOINT}/api/miniapp/cbe-superapp-login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ customer_identifier: customerIdentifier }),
        }
      );
      const json = await res.json().catch(() => null);
      console.log("status", res.status, json);

      if (json && json.status) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        setIsSending(false);
        sessionStorage.setItem("token", json.token);
        // CBE access token — reused later for the SuperApp payment request
        if (json.cbe_access_token) {
          sessionStorage.setItem("cbeAccessToken", json.cbe_access_token);
        }
        setToken(json.token);
        navigate("/tenders");
      } else if (json && json.phone) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        setIsSending(false);
        setCbePhone(json.phone);
        navigate("/signup");
      } else {
        // backend reachable but rejected the login — keep the raw
        // backend reason out of the UI, show a friendly message instead
        showError(
          "Couldn't Sign You In",
          "Something went wrong. Please try again."
        );
      }

      return json;
    } catch (error) {
      console.log("error", error);
      showError(
        "Connection Problem",
        "We couldn't reach iChereta. Check your connection and try again."
      );
      return null;
    }
  };

  // clean up the SuperApp callback handler + timer when leaving the page
  useEffect(() => {
    return () => {
      delete window.handleAccessToken;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const logUserIn = () => {
    // the SuperApp injects `window.cbesuperapp` only inside its WebView
    if (!window.cbesuperapp || typeof window.cbesuperapp.send !== "function") {
      setAlert({
        title: "Open in CBE SuperApp",
        message:
          "This app needs to be opened from within the CBE SuperApp. Please launch iChereta from the CBE SuperApp to continue.",
      });
      return;
    }

    setIsSending(true);

    // if the SuperApp never calls back, don't spin forever
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      showError(
        "Taking Too Long",
        "We didn't hear back from the CBE SuperApp. Please try again."
      );
    }, LOGIN_TIMEOUT_MS);

    // callback the SuperApp invokes with the customer identifier
    window.handleAccessToken = (customerIdentifier) => {
      if (customerIdentifier) {
        cbeSuperAppLogin(customerIdentifier);
      } else {
        showError(
          "Couldn't Sign You In",
          "Something went wrong while signing you in. Please try again."
        );
      }
    };

    const payload = JSON.stringify({
      functionName: "fetchAccessToken",
      params: {
        appcode: MINIAPP_CODE,
        callbackName: "handleAccessToken",
      },
    });

    window.cbesuperapp.send(payload);
  };

  return (
    <AuthenticationLayout
      button="Sign Up"
      route="/signup"
      message="Don't have an account?"
      footerClassName="text-[#d8ccf7]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%] bg-gradient-to-t from-[#664bc7]/80 via-[#664bc7]/30 via-60% to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%]"
        style={{
          backgroundImage: `url(${LoginPattern})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom",
          opacity: 0.6,
        }}
      />
      <div className="relative z-10 w-full flex flex-col items-center space-y-5">
        <h1 className="font-bold text-lg text-[#5a4aa6]">ሁሉንም ጨረታ በአንድ ቦታ!</h1>
        <small className="text-center text-xs font-bold text-[#5a4aa6]">በመረጧቸው ዘርፎች ያሉ የጨረታ ዝርዝር መረጃዎችን በቀላሉ ይግኙ!</small>
        <div className=" animate-bounce">
          <ArrowIcon styles="w-8 h-8 rotate-90 text-[#5a4aa6]" />
        </div>
        <div className="w-[90%] max-w-[600px] bg-transparent flex flex-col flex-grow  sm:p-10 md:rounded-md space-y-3 md:space-y-5 ">
          <AuthenticationButton
            process={isSending}
            className="w-full h-12 md:h-[52px] bg-white text-[#3a2f6b] text-base md:text-lg rounded-xl overflow-hidden relative border border-gray-200 shadow-[0_2px_4px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.9)] hover:shadow-[0_4px_10px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)] active:translate-y-px transition-all duration-200"
            name={
              <span className="flex items-center justify-center gap-3 font-bold">
                <img
                  src={CBELogo}
                  alt="CBE SuperApp"
                  decoding="sync"
                  loading="eager"
                  className="h-7 w-7 object-contain"
                />
                <span className="h-6 w-px bg-gray-300" />
                Login with CBE SuperApp
              </span>
            }
            onClick={logUserIn}
          />
        </div>
      </div>

      {alert && (
        <AlertModal
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </AuthenticationLayout>
  );
};

export default LoginPage;
